'use client';
import type { ReactElement, ReactNode } from 'react';
import { Children, isValidElement, memo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useMediaQuery } from 'usehooks-ts';

import { FormControl } from '@ui/uikit/components/common/FormControl';
import { Select, SelectOption } from '@ui/uikit/components/common/Select';
import { cn } from '@ui/uikit/utils';

import styles from './Tabs.module.scss';

interface TabsProps {
  defaultActiveTabId?: string;
  children: ReactNode;
}

interface TabProps {
  label: string;
  children: ReactNode;
  id?: string;
  disabled?: boolean;
}

const TabsComponent = ({ children, defaultActiveTabId }: TabsProps) => {
  const tabs = Children.toArray(children)
    .filter(isValidElement) as ReactElement<TabProps>[];

  const firstEnabledTab = tabs.find((tab) => !tab.props.disabled);
  const [activeTab, setActiveTab] = useState<string>(
    defaultActiveTabId ?? firstEnabledTab?.props.id ?? tabs[0]?.props.id ?? '0',
  );

  const [loadedTabs, setLoadedTabs] = useState<string[]>([activeTab]);

  const handleTabClick = (tabId: string, disabled?: boolean) => {
    if (disabled) {
      return;
    }

    setActiveTab(tabId);

    if (!loadedTabs.includes(tabId)) {
      setLoadedTabs([...loadedTabs, tabId]);
    }
  };

  const isMobile = useMediaQuery('(max-width: 767px)');

  const { control } = useForm<{
    activeTab: string;
  }>({
    defaultValues: {
      activeTab,
    },
  });

  if (!tabs.length) {
    return null;
  }

  return (
    <div className={styles['container']}>
      {isMobile ? (
        <nav>
          <Controller
            control={control}
            name="activeTab"
            render={({ field }) => (
              <FormControl label="Reason for the Report">
                <Select onValueChange={handleTabClick} value={activeTab} portalMode="overlay">
                  {tabs.map((tab, idx) => {
                    const tabId = tab.props.id ?? String(idx);
                    return (
                      <SelectOption key={tabId} value={tabId} disabled={tab.props.disabled}>
                        {tab.props.label}
                      </SelectOption>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          />
        </nav>
      ) : (
        <nav className={styles['tabs']}>
          {tabs.map((tab, idx) => {
            const tabId = tab.props.id ?? String(idx);
            return (
              <button
                key={tabId}
                onClick={() => handleTabClick(tabId, tab.props.disabled)}
                className={cn(styles['tabs__tab'], {
                  [styles['tabs__tab_active']]: activeTab === tabId,
                  [styles['tabs__tab_disabled']]: tab.props.disabled,
                })}
                disabled={tab.props.disabled}
              >
                {tab.props.label}
              </button>
            );
          })}
        </nav>
      )}

      <div>
        {tabs.map((tab, idx) => {
          const tabId = tab.props.id ?? String(idx);

          if (!loadedTabs.includes(tabId)) {
            return null;
          }

          return (
            <div
              key={tabId}
              className={cn({
                [styles['tabText_visible']]: activeTab === tabId,
                [styles['tabText_hidden']]: activeTab !== tabId,
              })}
            >
              {tab.props.children}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Tab = ({ children }: TabProps) => children;

const Tabs = memo(TabsComponent);

export { Tabs, Tab };
